
import { useState } from 'react';

export default function CharterInquirySection() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    destination: '',
    charterDates: '',
    duration: '',
    guests: '',
    budget: '',
    yachtPreference: '',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      await fetch('https://readdy.ai/api/form/d632p52bdoed7v0v8jr0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        destination: '',
        charterDates: '',
        duration: '',
        guests: '',
        budget: '',
        yachtPreference: '',
        specialRequests: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 px-8 lg:px-16 bg-stone-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <div className="w-20 h-px bg-black mx-auto mb-8"></div>
          <h2 className="font-serif text-4xl md:text-5xl text-black mb-6">
            Charter Inquiry
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Begin your bespoke charter experience. Share your preferences and our expert team will curate the perfect voyage for you.
          </p>
        </div>

        {isSubmitted ? (
          <div className="bg-white p-12 text-center border border-stone-200">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-white text-2xl"></i>
            </div>
            <h3 className="font-serif text-2xl text-black mb-4">Thank You for Your Inquiry</h3>
            <p className="text-gray-600 mb-8">
              Our charter specialists will review your requirements and contact you within 24 hours to discuss your bespoke voyage.
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="px-8 py-3 bg-black text-white text-sm tracking-widest uppercase hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Submit Another Inquiry
            </button>
          </div>
        ) : (
          <form
            id="charter-inquiry-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white p-8 md:p-12 border border-stone-200"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm"
                  placeholder="Your first name"
                />
              </div>
              <div>
                <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm"
                  placeholder="Your last name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                  Preferred Destination *
                </label>
                <select
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                >
                  <option value="">Select destination</option>
                  <option value="Mediterranean">Mediterranean</option>
                  <option value="French Riviera">French Riviera</option>
                  <option value="Greece">Greece</option>
                  <option value="Italy">Italy</option>
                  <option value="Croatia">Croatia</option>
                  <option value="Caribbean">Caribbean</option>
                  <option value="Bahamas">Bahamas</option>
                  <option value="British Virgin Islands">British Virgin Islands</option>
                  <option value="South Pacific">South Pacific</option>
                  <option value="Maldives">Maldives</option>
                  <option value="Thailand">Thailand</option>
                  <option value="Norway">Norway</option>
                  <option value="Alaska">Alaska</option>
                  <option value="Antarctica">Antarctica</option>
                  <option value="Other">Other / Flexible</option>
                </select>
              </div>
              <div>
                <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                  Preferred Charter Dates
                </label>
                <input
                  type="text"
                  name="charterDates"
                  value={formData.charterDates}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm"
                  placeholder="e.g., July 15-22, 2025"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div>
                <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                  Charter Duration
                </label>
                <select
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                >
                  <option value="">Select duration</option>
                  <option value="3-4 days">3-4 Days</option>
                  <option value="1 week">1 Week</option>
                  <option value="10 days">10 Days</option>
                  <option value="2 weeks">2 Weeks</option>
                  <option value="3+ weeks">3+ Weeks</option>
                </select>
              </div>
              <div>
                <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                  Number of Guests
                </label>
                <select
                  name="guests"
                  value={formData.guests}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                >
                  <option value="">Select guests</option>
                  <option value="2">2 Guests</option>
                  <option value="4">4 Guests</option>
                  <option value="6">6 Guests</option>
                  <option value="8">8 Guests</option>
                  <option value="10">10 Guests</option>
                  <option value="12+">12+ Guests</option>
                </select>
              </div>
              <div>
                <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                  Budget Range
                </label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                >
                  <option value="">Select budget</option>
                  <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                  <option value="$100,000 - $250,000">$100,000 - $250,000</option>
                  <option value="$250,000 - $500,000">$250,000 - $500,000</option>
                  <option value="$500,000 - $1,000,000">$500,000 - $1,000,000</option>
                  <option value="$1,000,000+">$1,000,000+</option>
                </select>
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                Yacht Preference
              </label>
              <select
                name="yachtPreference"
                value={formData.yachtPreference}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
              >
                <option value="">Select yacht type</option>
                <option value="Motor Yacht">Motor Yacht</option>
                <option value="Sailing Yacht">Sailing Yacht</option>
                <option value="Catamaran">Catamaran</option>
                <option value="Explorer Yacht">Explorer Yacht</option>
                <option value="Classic Yacht">Classic Yacht</option>
                <option value="No Preference">No Preference</option>
              </select>
            </div>

            <div className="mb-8">
              <label className="block text-xs tracking-widest text-gray-500 uppercase mb-2">
                Special Requests or Requirements
              </label>
              <textarea
                name="specialRequests"
                value={formData.specialRequests}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-stone-300 focus:border-black focus:outline-none transition-colors text-sm resize-none"
                placeholder="Tell us about any special occasions, dietary requirements, activities you'd like to experience, or specific yachts you're interested in..."
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {formData.specialRequests.length}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-black text-white text-sm tracking-widest uppercase hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Charter Inquiry'}
            </button>

            <p className="text-xs text-gray-500 text-center mt-6">
              By submitting this form, you agree to be contacted by our charter specialists regarding your inquiry.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
